import React, { useReducer, Dispatch } from 'react';
import sendMessage from 'utils/sendMessage';
import { ERROR_MESSAGE, CLEAR_ERRORS } from 'utils/constants';
import { getQueryAsObject } from 'utils/query';
import { ActionType, ActionMap } from './baseTypes';

enum DispatchTypes {
  SET_ALERT_MESSAGE = 'SET_ALERT_MESSAGE',
  SET_USE_MESSAGING = 'SET_USE_MESSAGING',
}

type AlertActionType = ActionType<DispatchTypes>;

export type AlertDispatch = Dispatch<AlertActionType>;

export type Alert = {
  text: string | undefined;
  severity: 'error' | 'warning' | 'info' | 'success';
  useMessaging: boolean;
};

const actions = {
  [DispatchTypes.SET_ALERT_MESSAGE]: (state: Alert, { text, severity }) => ({
    ...state,
    text,
    severity,
  }),
  [DispatchTypes.SET_USE_MESSAGING]: (state: Alert, useMessaging: boolean) => ({
    ...state,
    useMessaging,
  }),
} as ActionMap<Alert>;

export const alertActions = {
  setAlert: (text?: string | JSX.Element, severity?: Alert['severity']) => (
    dispatch: AlertDispatch,
    state: Alert,
  ): void => {
    if (state.useMessaging && severity === 'error' && !(text && React.isValidElement(text))) {
      if (text) {
        sendMessage(ERROR_MESSAGE, text);
      } else {
        sendMessage(CLEAR_ERRORS);
        // Clear any non-error messages from alerts
        dispatch({ type: DispatchTypes.SET_ALERT_MESSAGE, payload: { text } });
      }
    } else {
      dispatch({ type: DispatchTypes.SET_ALERT_MESSAGE, payload: { text, severity } });
    }
  },
  setError: (text?: string | JSX.Element): ((dispatch: AlertDispatch, state: Alert) => void) => alertActions.setAlert(text, 'error'),
};

const reducer = (state: Alert, { type, payload }: AlertActionType): Alert => actions[type]?.(state, payload) || state || null;

const { sendErrorsToTop }: { sendErrorsToTop?: boolean } = getQueryAsObject();

const initState: Alert = {
  text: '',
  severity: 'error',
  useMessaging: !!sendErrorsToTop,
};

export const useAlertReducer = (): [Alert, AlertDispatch] => useReducer(reducer, initState);
